import ProjectAPI from '@apis/ProjectAPI';
import {
  Divider,
  Modal,
  Stack,
  Title,
  Text,
  TextInput,
  Group,
  Button,
} from '@mantine/core';
import ProjectModel from '@models/ProjectModel';
import { useCallback, useState } from 'react';
import APIErrorMessage from './APIErrorMessage';
import { useQueryClient } from '@tanstack/react-query';
import { Summary } from '@hooks/useProjectSummaries';

interface DeleteSummaryModalProps {
  project: ProjectModel;
  summary: Summary;
  opened: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteSummaryModal = ({
  project,
  opened,
  onClose,
  onDeleted,
  summary,
}: DeleteSummaryModalProps) => {
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteError, setDeleteError] = useState<React.ReactNode | undefined>(
    undefined
  );
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const queryClient = useQueryClient();

  const deleteSummary = useCallback(() => {
    setDeleteInProgress(true);

    const updatedProject = {
      ...project,
      deletedSummaries: [{
        Id: summary.id,
        Name: summary.name, 
        AnalysisIds: summary.analyses.map(a => a.id), 
        CreatedAt: summary.createdAt.toString(), 
        UpdatedAt: summary.updatedAt.toString(),
      }],
    };

    ProjectAPI.updateProject(updatedProject)
      .then((data) => {
        queryClient.setQueryData(['project', data.projectId], data);
        setDeleteError(undefined);
        setDeleteInProgress(false);
        setDeleteConfirmationText('');
        onDeleted();
      })
      .catch((error) => {
        console.log(error);
        setDeleteInProgress(false);
        setDeleteError(
          <APIErrorMessage
            response={error}
            dismissable={true}
            simple
            onDismiss={() => setDeleteError(undefined)}
          />
        );
      });
  }, [project, summary, onDeleted, queryClient]);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setDeleteConfirmationText('');
        onClose();
      }}
      withCloseButton={false}
      size="lg"
    >
      <Stack>
        <Title order={3}>Delete Plan of Action</Title>
        <Divider />
      </Stack>
      <Stack style={{ marginTop: '10px' }}>
        <Text>
          You are about to delete the plan of action <b>{summary.name}</b>
        </Text>
        <TextInput
          label={
            <Text>
              To confirm the deletion of this plan of action, type <i>delete</i> in
              the box below
            </Text>
          }
          description={<Text color="orange">This action cannot be undone</Text>}
          placeholder="delete"
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          onChange={(event) =>
            setDeleteConfirmationText(event.currentTarget.value)
          }
        />
        {deleteError}
        <Group position="right">
          <Button
            variant="subtle"
            color="dark"
            onClick={() => {
              setDeleteConfirmationText('');
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            color="red"
            disabled={deleteConfirmationText !== 'delete'}
            loading={deleteInProgress}
            onClick={deleteSummary}
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
